/**
 * The "…" menu of a page post: what can be done depends on its status —
 * edit, publish a draft, retry a failed post, cancel a schedule, open on
 * Facebook, delete. Deleting and cancelling ask first. Calls the posts API
 * and tells the caller to reload.
 */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { ConfirmationModal } from '../Modal';
import { colors, spacing, typography } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import type { RecentPost } from '../../screens/dashboard/RecentPostsCard';

interface Props {
  post: RecentPost | null;
  canManage: boolean;
  onClose: () => void;
  /** After a change: reload the list (deleted=true when the post is gone). */
  onChanged: (deleted?: boolean) => void;
  onEdit: (post: RecentPost) => void;
}

type Confirm = 'delete' | 'cancel' | null;

export const PostActionsSheet: React.FC<Props> = ({ post, canManage, onClose, onChanged, onEdit }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { showNotification } = useAppContext();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  if (!post) return null;

  const fail = (res: any, fallback: string) => {
    const code = res?.additionalInfo?.errorCode;
    showNotification('error', fallback, code === 'missing_permissions' ? t('posts.error.permissions') : (res?.errors?.[0]?.message || ''));
  };

  const run = async (call: () => Promise<any>, success: string, failure: string, deleted = false) => {
    setBusy(true);
    try {
      const res = await call();
      if (res.statusCode === 1000) {
        showNotification('success', success, '');
        setConfirm(null);
        onClose();
        onChanged(deleted);
      } else {
        fail(res, failure);
      }
    } catch (err: any) {
      showNotification('error', failure, err?.message || '');
    } finally {
      setBusy(false);
    }
  };

  const items: Array<{ key: string; icon: string; label: string; onPress: () => void; danger?: boolean }> = [];
  if (post.permalinkUrl && post.status === 'published') {
    items.push({ key: 'open', icon: 'open-outline', label: t('posts.action.openOnFacebook'), onPress: () => { Linking.openURL(post.permalinkUrl!); onClose(); } });
  }
  if (canManage) {
    if (post.status === 'draft') {
      items.push({ key: 'publish', icon: 'paper-plane-outline', label: t(post.scheduledFor ? 'posts.action.schedule' : 'posts.action.publishNow'), onPress: () => run(() => apiService.publishFacebookPost(post.postId), t('posts.done.published'), t('posts.failed.publish')) });
    }
    if (post.status === 'failed') {
      items.push({ key: 'retry', icon: 'refresh-outline', label: t('posts.action.retry'), onPress: () => run(() => apiService.publishFacebookPost(post.postId), t('posts.done.published'), t('posts.failed.publish')) });
    }
    items.push({ key: 'edit', icon: 'create-outline', label: t(post.status === 'published' ? 'posts.action.editText' : 'posts.action.edit'), onPress: () => { onClose(); onEdit(post); } });
    if (post.status === 'scheduled') {
      items.push({ key: 'cancel', icon: 'close-circle-outline', label: t('posts.action.cancelSchedule'), onPress: () => setConfirm('cancel') });
    }
    items.push({ key: 'delete', icon: 'trash-outline', label: t('posts.action.delete'), danger: true, onPress: () => setConfirm('delete') });
  }

  return (
    <>
      <Modal visible={!!post && !confirm} animationType="slide" transparent onRequestClose={onClose}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={busy ? undefined : onClose}>
          <View style={styles.sheet}>
            {items.map((item) => (
              <TouchableOpacity
                key={item.key}
                style={[styles.row, { flexDirection: rtl.rowDirection }]}
                onPress={item.onPress}
                disabled={busy}
                accessibilityRole="button"
              >
                <Ionicons name={item.icon as any} size={20} color={(item.danger ? colors.error : colors.text.primary) as string} />
                <Text style={[styles.rowText, { textAlign: rtl.textStart }, item.danger && styles.danger]}>{item.label}</Text>
              </TouchableOpacity>
            ))}
            {items.length === 0 && <Text style={styles.empty}>{t('posts.noActions')}</Text>}
            {busy && <ActivityIndicator style={styles.busy} color={colors.primary as string} />}
            <TouchableOpacity style={styles.cancelRow} onPress={onClose} disabled={busy}>
              <Text style={styles.cancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <ConfirmationModal
        visible={confirm === 'delete'}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={() => run(() => apiService.deleteFacebookPost(post.postId), t('posts.done.deleted'), t('posts.failed.delete'), true)}
        title={t('posts.confirm.deleteTitle')}
        message={t(post.status === 'published' || post.status === 'scheduled' ? 'posts.confirm.deleteLive' : 'posts.confirm.deleteLocal')}
        confirmText={t('posts.action.delete')}
        cancelText={t('common.cancel')}
        type="error"
        loading={busy}
      />
      <ConfirmationModal
        visible={confirm === 'cancel'}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={() => run(() => apiService.cancelFacebookPostSchedule(post.postId), t('posts.done.unscheduled'), t('posts.failed.cancel'))}
        title={t('posts.action.cancelSchedule')}
        message={t('posts.confirm.cancelSchedule')}
        confirmText={t('posts.action.cancelSchedule')}
        cancelText={t('common.cancel')}
        type="warning"
        loading={busy}
      />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end', alignItems: 'center' },
  sheet: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingVertical: spacing.sm,
    paddingBottom: spacing.lg,
  },
  row: { alignItems: 'center', gap: spacing.md, paddingHorizontal: spacing.lg, paddingVertical: spacing.md },
  rowText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary },
  danger: { color: colors.error },
  empty: { padding: spacing.lg, textAlign: 'center', color: colors.text.secondary },
  busy: { marginVertical: spacing.sm },
  cancelRow: { marginTop: spacing.xs, paddingVertical: spacing.md, alignItems: 'center', borderTopWidth: 1, borderTopColor: colors.border },
  cancelText: { fontSize: typography.fontSize.base, color: colors.text.secondary, fontFamily: typography.fontFamily.semibold },
});
